// services/priceCalculator.js
import { EXTRAS_PRICING } from '../utils/extrasConfig';

class PriceCalculationService {
  /**
   * Helper to find peak multiplier for a given pickup time
   * @param {Array} multipliers - Array of peak windows
   * @param {string|Date} pickupAt - ISO string or Date object
   */
  getPeakMultiplier(multipliers = [], pickupAt) {
    if (!multipliers || multipliers.length === 0 || !pickupAt) return 1;

    const date = new Date(pickupAt);
    const dayOfWeek = date.getDay(); // 0 (Sun) to 6 (Sat)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const pickupTotalMinutes = hours * 60 + minutes;

    const activePeak = multipliers.find(window => {
      // API may use 1-7 for Mon-Sun or 0-6
      // Assuming 1 = Monday, ..., 7 = Sunday per standard API patterns
      // Convert JS getDay() (0=Sun, 1=Mon) to matching format
      const apiDay = dayOfWeek === 0 ? 7 : dayOfWeek;

      if (window.dayOfWeek !== apiDay) return false;

      // startTime/endTime e.g. "08:00"
      const [startH, startM] = window.startTime.split(':').map(Number);
      const [endH, endM] = window.endTime.split(':').map(Number);
      const startTotalMinutes = startH * 60 + startM;
      const endTotalMinutes = endH * 60 + endM;

      return pickupTotalMinutes >= startTotalMinutes && pickupTotalMinutes <= endTotalMinutes;
    });

    return activePeak ? activePeak.multiplier : 1;
  }

  calculateTransferPrice(vehicle, distanceKm, pickupAt, extrasData = {}, returnMultiplier = 1, waitingMins = 0) {
    const pricing = vehicle?.pricing;
    if (!pricing) return null;

    // Robust breakdown selection: merge breakdown into pricing to catch all fields
    const pb = {
      ...(pricing || {}),
      ...(pricing?.breakdown || {})
    };

    const baseFare = Number(pb.baseFare || 0);
    const pricePerKm = Number(pb.pricePerKm || pb.distanceRateKm || 0);

    const distanceCharge = distanceKm * pricePerKm;
    // Rule: Max(Base Fare, Distance × Price per Km). Ignore duration.
    const computedBase = Math.max(baseFare, distanceCharge);

    console.log('🔧 calculateTransferPrice:', {
      distanceKm,
      baseFare,
      pricePerKm,
      distanceCharge,
      computedBase,
      'pb keys': Object.keys(pb),
      pb,
    });

    const peakMultiplier = this.getPeakMultiplier(pb.peakMultipliers, pickupAt);
    const peakSurcharge = computedBase * (peakMultiplier - 1);

    // Waiting charge logic
    const freeWaitingMins = Number(pb.freeWaitingMins || 0);
    const waitingChargePerMin = Number(pb.waitingChargePerMin || 0);
    const waitingBillable = Math.max(0, waitingMins - freeWaitingMins);
    const waitingCharge = waitingBillable * waitingChargePerMin;

    // Extras logic
    let extrasTotal = 0;
    if (extrasData) {
      extrasTotal += (extrasData.childSeats || 0) * (EXTRAS_PRICING.childSeat || 0);
      extrasTotal += (extrasData.boosterSeats || 0) * (EXTRAS_PRICING.boosterSeat || 0);
      if (extrasData.meetGreet) extrasTotal += (EXTRAS_PRICING.meetGreet || 0);
    }

    const outwardSubtotal = computedBase + peakSurcharge + waitingCharge + extrasTotal;
    const finalAmount = outwardSubtotal * returnMultiplier;

    return {
      total: Math.round(finalAmount * 100) / 100,
      baseFare: computedBase,
      peakSurcharge,
      waitingCharge,
      extrasTotal,
      currency: pricing?.currency || 'USD'
    };
  }

  calculateHourlyPrice(vehicle, hours, distanceKm, pickupAt) {
    const pricing = vehicle?.pricing;
    if (!pricing) return { total: 0, currency: 'USD' };

    // Robust breakdown selection: merge breakdown into pricing to catch all fields
    const pb = {
      ...(pricing || {}),
      ...(pricing?.breakdown || {})
    };

    const hourlyRate = Number(pb.baseHourRate || pb.hourlyRate || 0);
    const hourlyBase = hourlyRate * hours;

    const includedKmPerHour = Number(pb.includedKmPerHour || 0);
    const hourlyIncludedKm = hours * includedKmPerHour;
    const overagePerKm = Number(pb.overagePerKm || 0);
    const hourlyOverKm = Math.max(0, distanceKm - hourlyIncludedKm);
    const hourlyOverKmCharge = hourlyOverKm * overagePerKm;

    const peakMultiplier = this.getPeakMultiplier(pb.peakMultipliers, pickupAt);
    const peakSurcharge = hourlyBase * (peakMultiplier - 1);

    const totalAmount = hourlyBase + peakSurcharge + hourlyOverKmCharge;

    return {
      total: Math.round(totalAmount * 100) / 100,
      basePrice: hourlyBase,
      peakSurcharge,
      overKmCharge: hourlyOverKmCharge,
      hours: hours,
      includedKm: hourlyIncludedKm,
      currency: pricing?.currency || 'USD'
    };
  }

  getVehicleImage(vehicle) {
    if (!vehicle) return null;
    console.log('🖼️ getVehicleImage for:', vehicle.className, {
      image: vehicle.image,
      image_data: vehicle.image_data,
      has_nested_data: !!(vehicle.image && vehicle.image.data)
    });

    // Direct Image URL Support (New API Format)
    if (typeof vehicle.image === 'string' && vehicle.image.startsWith('http')) {
      return vehicle.image;
    }

    // Support both flattened and nested image fields (Legacy/Deprecated)
    const imageData = vehicle.image_data || (vehicle.image && vehicle.image.data);
    const mimeType = vehicle.image_mime_type || (vehicle.image && vehicle.image.mimeType) || 'image/png';

    if (imageData) {
      // If it already starts with data:, return it (it might be a full data URI)
      if (typeof imageData === 'string' && imageData.startsWith('data:')) {
        // Basic check if it's just a header like "data:image/png;" without content
        if (imageData.length < 30) return null;
        return imageData;
      }
      return `data:${mimeType};base64,${imageData}`;
    }
    return null;
  }

  isSearchValid(validUntil) {
    if (!validUntil) return false;
    return new Date(validUntil) > new Date();
  }
}

const service = new PriceCalculationService();
export default service;